<template>
  <div class="d-flex align-items-center justify-content-center h-400" v-if="loading">
    <span class="spinner-border"></span>
  </div>
  <div class="change-profile-wrapper" v-else>
    <!-- A failed load is not "only one profile": saying so sends people with several
         profiles to support instead of trying again. -->
    <div v-if="loadFailed" class="d-flex flex-column p-4">
      We couldn't load your profiles just now. Please try again.
      <div class="d-flex flex-column my-4 gap-4 align-items-center">
        <button class="ldna-btn" @click="loadProfiles">
          Try again
        </button>
      </div>
    </div>
    <div class="gap-0 overflow-auto py-0 change-profile-scroll" v-else-if="visibleRows.length >= 1">
      <div class="add-profile-card" @click="addProfile('add-profile')">
        <div>
          <img src="@/assets/images/add-profile.svg" alt="Add-Profile" />
          <span>Add Profile</span>
        </div>
      </div>
      <p class="mt-4">{{ visibleRows.length > 1 ? 'Select a profile below to switch' : 'Your profile' }}</p>
      <!-- The whole row switches, not only the radio, so it's easy to tap on a phone -->
      <div class="profile-row-wrapper mt-0 rounded-4 p-3" :class="{ 'selected-profile': isGroupDefault(profile) }"
        v-for="profile in visibleRows" :key="profile.barcode" @click="selectProfile(profile)">
        <div>
          <!-- flex row  -->
          <div class="profile-flex-row d-flex">
            <div>
              <img v-if="profile.profile?.photo" :src="profile.profile?.photo" />
              <img class="border border-3" src="@/assets/profile.png" v-else />
            </div>
            <div class="overflow-hidden">
              <p
                class="truncate"
                :title="profile.profile?.name.length > 12 ? profile.profile?.name : ''">
                {{ profile.profile?.name }}
              </p>
              <p class="d-flex align-items-center m-0">
                <img :src="SingleUserIon" alt="" />
                <span class="truncate" v-if="profile.permission?.type === 'OWNER'" title="You own this profile">You own this profile</span>
                <span class="truncate" v-else-if="profile.permission?.type === 'MANAGER'" title="You manage this profile">You manage this profile</span>
                <span class="truncate" v-else-if="profile.permission?.type === 'PARENT'" title="This is your child's profile">This is your child's profile</span>
              </p>
            </div>
            <div class="text-body-secondary">
              <p class="m-0 truncate"
                :title="kitLabel(profile).length > 20 ? kitLabel(profile) : ''">
                {{ kitLabel(profile) }}
                <span v-if="profile.isEpigeneticsGroup && profile.epiKits.length > 1" class="epi-kit-count">
                  {{ profile.epiKits.length }} kits
                </span>
              </p>
              <p class="m-0">{{ profile.isEpigeneticsGroup && profile.epiKits.length > 1 ? '–' : profile.barcode }}</p>
            </div>
          </div>
          <!-- flex row -->
        </div>
        <div class="d-flex justify-content-center align-items-center">
          <!-- radio button row -->
          <div class="form__radio-group" @click.stop>
            <input :id="'radio-' + profile.barcode" type="radio" class="form__radio-input" name="selectedProfile"
              :checked="isGroupDefault(profile)" @click="selectProfile(profile)" />
            <label :for="'radio-' + profile.barcode" class="form__radio-label">
              <span class="form__radio-button"></span>
            </label>
          </div>
          <!-- radio button row -->
        </div>
      </div>
      <!-- profile row -->
    </div>
    <div v-else class="d-flex flex-column p-4">
      As you only have one profile you aren't able to switch. You might want to add more
      profiles in order to manage a family member's DNA or upload your DNA from other
      testing sites.
      <div class="d-flex flex-column my-4 gap-4 align-items-center">
        <button class="ldna-btn" @click="gotoPage('profile-who-does-this-dna-belong-to')">
          Upload DNA
        </button>
        <button class="ldna-btn ldna-btn--secondary" @click="gotoPage('profile-activate-dna-kit')">
          Activate kit
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import SingleUserIon from "@/assets/images/Icons/svgviewer-output (1) 1.png";
import { kitLabel as kitLabelFor } from "@/services/kitService";
import { useAccountProfiles } from "@/composables/useAccountProfiles";

import { ref, onMounted, computed, defineEmits, defineProps } from "vue";
import { useRouter } from "vue-router";
import { HEALTH } from "@/composables/useProfileDomain";

// Optional domain filter: when the nav opens this as a "choose which profile" step it
// passes ANCESTRY or HEALTH so only that domain's rows show; unset shows everything.
const props = defineProps({ domain: { type: String, default: null } });
const emits = defineEmits(["close"]);
const router = useRouter();

// Load + grouping (ancestry rows + one row per epigenetic person) lives in the shared
// composable so this switcher and the Ancestry/Health nav resolution never drift.
const { loading, rows: mergedTestAndProfile, load, switchToProfile } = useAccountProfiles();

const visibleRows = computed(() => {
  if (!props.domain) return mergedTestAndProfile.value;
  const wantHealth = props.domain === HEALTH;
  return mergedTestAndProfile.value.filter((r) => !!r.isEpigeneticsGroup === wantHealth);
});

const loadFailed = ref(false);

const loadProfiles = async () => {
  loadFailed.value = false;
  try {
    await load();
  } catch (err) {
    console.error("Could not load the account's profiles", err);
    loadFailed.value = true;
  }
};

onMounted(loadProfiles);

const isGroupDefault = (profile) => {
  if (!profile.isEpigeneticsGroup) return profile.permission?.isDefault;
  return profile.epiKits.some(k => k.permission?.isDefault);
};

const selectProfile = async (profile) => {
  try {
    await switchToProfile(profile);
    if (profile.barcode?.endsWith('E')) {
      emits("close");
      router.push({ name: 'epigenetics-overview' });
    } else {
      router.go();
    }
  } catch (err) {
    console.log("Error occurred while changing profile", err);
  }
};

const gotoPage = (name) => {
  emits("close");
  router.push({ name });
};

const addProfile = (name) => {
  emits("close");
  router.push({ name });
};

const kitLabel = (profile) => kitLabelFor(profile.barcode, profile.product_sku);
</script>

<style lang="scss">
.add-profile-card {
  height: 64px;
  background: rgba(235, 242, 255, 1);
  border: 1px rgba(51, 119, 255, 1);
  box-shadow: 0px 4px 20px 0px rgba(238, 238, 238, 0.502);
  border-radius: 14px;
  border-style: dashed;
  display: flex;
  padding: 10px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    border-style: solid;
  }
}

.add-profile-card div {
  display: flex;
  gap: 8px;
  height: 24px;
  font-size: 14px;
  color: rgba(51, 119, 255, 1);
  align-items: center;
}

.change-profile-wrapper {
  margin-top: -15px;
}

.change-profile-scroll {
  max-height: calc(100dvh - 200px);
  scrollbar-width: thin;
  padding-right: 5px;
}

.change-profile-wrapper>p {
  color: #1b1b1b;
  font-weight: 400;
  font-size: 16px;
}

.change-profile-scroll .profile-row-wrapper {
  cursor: pointer;
}

.profile-row-wrapper {
  border: 1px solid #d0d0d0;
  padding: 20px 20px;
  border-radius: 20px;
  margin: 15px 0px;
  display: grid;
  grid-template-columns: 4fr 0.6fr;
  gap: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.profile-row-wrapper>div {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.profile-flex-row {
  display: grid !important;
  grid-template-columns: 50px 1fr 190px;
  gap: 20px;

  >div>img {
    width: 50px;
    height: 50px;
    border-radius: 50%;
  }
}

.profile-flex-row>div:nth-child(2)>p:nth-child(3) {
  margin-bottom: 0rem;
}

.profile-flex-row>div:nth-child(2)>p:nth-child(1) {
  color: #1b1b1b;
  font-weight: 500;
  font-size: 16px;
  margin-bottom: -0.2rem;
}

.profile-flex-row>div:nth-child(2)>p:nth-child(2) {
  color: #00000099;
  font-weight: 500;
  font-size: 13px;
  margin-bottom: 0.1rem;
}

.profile-flex-row>div:nth-child(2)>p:nth-child(3) {
  color: #b6b6b6;
  font-weight: 500;
  font-size: 12px;
}
.form__radio-input {
  display: none;
}

.form__radio-label {
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 1rem;
  font-size: 1.5rem;
  cursor: pointer;
}

.form__radio-button {
  height: 2rem;
  width: 2rem;
  border: 5px solid #d9d9d9;
  border-radius: 50%;
  display: inline-block;
  position: relative;
  transform: translateY(-2px);
}

.form__radio-button::after {
  content: "";
  display: block;
  height: 1rem;
  width: 1rem;
  position: absolute;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transition: opacity 0.1s;
  transform: translate(-50%, -50%);
  background-color: #3377ff;
  opacity: 0;
}

.selected-profile {
  border-color: #3377ff;
}

.epi-kit-count {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #ebf2ff;
  color: #3377ff;
  font-size: 11px;
  font-weight: 600;
  vertical-align: middle;
}

.form__radio-input:checked+.form__radio-label .form__radio-button::after {
  opacity: 1;
}
@media screen and (min-width: 599px) {
  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .overflow-hidden{
    overflow: hidden;
  }
}
@media screen and (max-width: 598px) {
  .profile-flex-row {
    flex-direction: column !important;
    display: flex !important;
  }

  .profile-flex-row>div:nth-child(3) {
    margin-right: auto !important;
    margin-left: initial !important;
  }
  .profile-flex-row > div:nth-child(2) > p:nth-child(2){
    line-height: 1.2;
    padding-top: 4px;
  }
  .form__radio-button{
    height: 1.5rem;
    width: 1.5rem;
    &::after{
      height: .6rem;
      width: .6rem;
    }
  }
  .profile-flex-row > div > img{
    width: 40px;
    height: 40px;
  }
  .truncate{
    text-align: left;
  }
}

// Phones: one compact row per profile - photo, name and who it belongs to, then the kit and
// barcode on one line - with the radio on the right, instead of a tall centred card each.
// Scoped to this list: the Help modal reuses these class names.
@media screen and (max-width: 598px) {
  .change-profile-scroll {
    .add-profile-card {
      height: 52px;
    }

    // SwitchHelp.vue makes every .profile-row-wrapper a centred column on phones
    .profile-row-wrapper {
      display: grid !important;
      text-align: left;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 12px;
      margin: 10px 0;
      padding: 12px 14px !important;
      border-radius: 14px !important;
    }

    .profile-flex-row {
      display: grid !important;
      grid-template-columns: 40px minmax(0, 1fr);
      grid-template-areas:
        "photo person"
        "photo kit";
      column-gap: 12px;
      row-gap: 4px;
      align-items: center;
      text-align: left;

      > div:nth-child(1) {
        grid-area: photo;
        align-self: start;
      }

      > div:nth-child(2) {
        grid-area: person;
        min-width: 0;
      }

      > div:nth-child(3) {
        grid-area: kit;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0 6px;
        min-width: 0;
        margin: 0 !important;
        font-size: 12px;

        > p {
          margin: 0;
        }

        > p + p::before {
          content: "·";
          margin-right: 6px;
        }
      }

      > div > img {
        width: 40px;
        height: 40px;
      }

      > div:nth-child(2) > p:nth-child(1) {
        margin-bottom: 2px;
        font-size: 15px;
      }

      > div:nth-child(2) > p:nth-child(2) {
        line-height: 1.3;
      }
    }

    .truncate {
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .form__radio-button {
      height: 1.5rem;
      width: 1.5rem;

      &::after {
        height: 0.6rem;
        width: 0.6rem;
      }
    }
  }
}
</style>
